import { useQuery, useMutation } from "@tanstack/react-query";
import { printError } from "helpers";
import {
  defaultTaskAdd,
  defaultTaskRemove,
  defaultTaskShow,
  templateTaskAdd,
  templateTaskDelete,
  templateTaskEdit,
  templateTaskShow,
} from "./modularApi";
import { useEffect } from "react";
import {
  addDefaultTutorialAction,
  addTemplateTutorialAction,
  deleteDefaultTutorialAction,
  deleteTemplateTutorialAction,
  getDefaultTutorialsAction,
  getTemplateTutorialsAction,
  updateTemplateTutorialAction,
} from "redux/reducers/templateLibrary/tutorials";
import { useUtilities } from "helpers";
import { ApiResponseType, NotificationType } from "enums";
import { t } from "i18next";

export const useTutorialsTemplate = () => {
  return useQuery(["tutorialTemplate"], () => templateTaskShow("tutorial"));
};

export function useTemplateTutorials() {
  const { data, isLoading, error } = useTutorialsTemplate();
  const {
    dispatch,
    tutorials: { tutorials, loaded },
  } = useUtilities();

  useEffect(() => {
    if (isLoading || loaded) return;
    if (data) {
      dispatch(getTemplateTutorialsAction(data.data));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, loaded]);

  return {
    data: tutorials,
    isLoading: isLoading || !loaded,
    error,
  };
}

export function useDeleteTemplateTutorial(redirect = "") {
  const { notification, dispatch, navigate } = useUtilities();
  return useMutation(templateTaskDelete, {
    onSuccess: (data) => {
      if (data.status_code === ApiResponseType.SUCCESS) {
        dispatch(deleteTemplateTutorialAction(data.id));
        notification({
          type: "success",
          message: t("libraryTemplate.tutorials.api.success.deleted"),
        });
        if (redirect) {
          navigate(redirect);
        }
      } else {
        notification({
          type: "error",
          message: printError(data.message),
        });
      }
    },
    onError: (error) => {
      console.log(error);
      notification({
        type: "error",
        message: t("common.error.somethingWentWrongTryLater"),
      });
    },
  });
}

export function useAddTemplateTutorial(duplicate = false, redirect = "") {
  const { dispatch, notification, navigate } = useUtilities();
  return useMutation(
    (params) =>
      templateTaskAdd({
        ...params,
        parent_type: "tutorial",
        module_hint: "tutorial",
      }),
    {
      onSuccess: (data) => {
        if (data.status_code === ApiResponseType.SUCCESS) {
          dispatch(addTemplateTutorialAction(data.data));
          notification({
            type: "success",
            message: duplicate
              ? t("libraryTemplate.tutorials.api.success.duplicated")
              : t("libraryTemplate.tutorials.api.success.added"),
          });
          if (redirect) {
            navigate(redirect + data.data.id);
          }
        } else {
          notification({
            type: NotificationType.ERROR,
            message: printError(data.message),
          });
        }
      },
      onError: () => {
        notification({
          type: "error",
          message: t("common.error.somethingWentWrongTryLater"),
        });
      },
    }
  );
}

export function useUpdateTemplateTutorial() {
  const { notification, dispatch } = useUtilities();
  return useMutation(
    (params) =>
      templateTaskEdit({
        ...params,
        parent_type: "tutorial",
        module_hint: "tutorial",
      }),
    {
      onSuccess: (data) => {
        if (data.status_code === ApiResponseType.SUCCESS) {
          dispatch(updateTemplateTutorialAction(data.data));
          notification({
            type: "success",
            message: t("libraryTemplate.tutorials.api.success.updated"),
          });
        } else {
          notification({
            type: NotificationType.ERROR,
            message: printError(data.message),
          });
        }
      },
      onError: () => {
        notification({
          type: NotificationType.ERROR,
          message: t("common.error.somethingWentWrongTryLater"),
        });
      },
    }
  );
}

export const useGetDefaultTutorials = () => {
  return useQuery(["tutorialDefault"], () => defaultTaskShow("tutorial"));
};

export function useDefaultTutorials() {
  const { data, isLoading, error } = useGetDefaultTutorials();
  const {
    dispatch,
    tutorials: {
      defaultSite: { tutorials, loaded },
    },
  } = useUtilities();

  useEffect(() => {
    if (loaded || isLoading) return;
    if (data) dispatch(getDefaultTutorialsAction(data.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, isLoading, data]);

  return {
    data: tutorials,
    isLoading: isLoading || !loaded,
    error,
  };
}

export function useRemoveDefaultTutorial() {
  const { notification, dispatch } = useUtilities();
  return useMutation(defaultTaskRemove, {
    onSuccess: (data) => {
      if (data.status_code === ApiResponseType.SUCCESS) {
        dispatch(deleteDefaultTutorialAction(data.id));
        notification({
          type: NotificationType.SUCCESS,
          message: t("libraryTemplate.tutorials.api.success.removedFromSiteTemplate"),
        });
      } else {
        notification({
          type: NotificationType.ERROR,
          message: printError(data.message),
        });
      }
    },
    onError: (error) => {
      console.log("error", error);
      notification({
        type: NotificationType.ERROR,
        message: t("libraryTemplate.tutorials.api.error.removedFromSiteTemplateFailed"),
      });
    },
  });
}

export function useAddDefaultTutorial() {
  const { notification, dispatch } = useUtilities();
  return useMutation(defaultTaskAdd, {
    onSuccess: (data) => {
      if (data.status_code === ApiResponseType.SUCCESS) {
        dispatch(addDefaultTutorialAction(data.data));
        notification({
          type: NotificationType.SUCCESS,
          message: t("libraryTemplate.tutorials.api.success.addedToSiteTemplate"),
        });
      } else {
        notification({
          type: NotificationType.ERROR,
          message: printError(data.message),
        });
      }
    },
    onError: (error) => {
      console.log("error", error);
      notification({
        type: NotificationType.ERROR,
        message: t("libraryTemplate.tutorials.api.error.addedToSiteTemplateFailed"),
      });
    },
  });
}
